import { apiFetch } from './api';

export type DraftStatus = 'draft' | 'sent' | 'archived';

export interface DraftSummary {
  id: string;
  title: string | null;
  status: DraftStatus;
  subject: string | null;
  currentVersion: number;
  updatedAt: number;
  previewHtml: string | null;
}

export interface DraftDetail {
  id: string;
  title: string | null;
  status: DraftStatus;
  current_version: number;
  subject: string | null;
  preview_text: string | null;
  from_email: string | null;
  from_name: string | null;
  source_prompt: string | null;
  style: string | null;
  language: string;
  created_at: number;
  updated_at: number;
}

export interface DraftContent {
  draft_id: string;
  version: number;
  html: string;
  design_json: string | null;
  change_summary: string | null;
  created_by: 'user' | 'ai' | 'system';
  created_at: number;
}

export interface DraftVersionMeta {
  version: number;
  change_summary: string | null;
  created_by: 'user' | 'ai' | 'system';
  created_at: number;
}

interface ListDraftRow {
  id: string;
  title: string | null;
  status: DraftStatus;
  subject: string | null;
  current_version?: number;
  updated_at?: number;
  preview_html?: string | null;
  /** Legacy camelCase payloads (pre snake_case list route fix). */
  currentVersion?: number;
  updatedAt?: number;
  previewHtml?: string | null;
}

interface ListResponse {
  drafts: ListDraftRow[];
}

export async function listDrafts(opts?: { status?: DraftStatus }): Promise<DraftSummary[]> {
  const params = new URLSearchParams();
  if (opts?.status) params.set('status', opts.status);
  const qs = params.toString();
  const data = await apiFetch<ListResponse>(`/api/drafts${qs ? `?${qs}` : ''}`);
  return data.drafts.map((d) => ({
    id: d.id,
    title: d.title,
    status: d.status,
    subject: d.subject,
    currentVersion: d.current_version ?? (d as { currentVersion?: number }).currentVersion ?? 0,
    updatedAt: d.updated_at ?? (d as { updatedAt?: number }).updatedAt ?? 0,
    previewHtml:
      d.preview_html?.trim() ||
      (d as { previewHtml?: string | null }).previewHtml?.trim() ||
      null,
  }));
}

export async function createDraft(input: { title?: string; html?: string; design_json?: string } = {}) {
  return apiFetch<{ draftId: string; version: number }>('/api/drafts', {
    method: 'POST',
    json: input,
  });
}

export async function getDraft(id: string): Promise<DraftDetail> {
  return apiFetch<DraftDetail>(`/api/drafts/${id}`);
}

export async function getDraftContent(id: string, version?: number): Promise<DraftContent> {
  const qs = version ? `?version=${version}` : '';
  return apiFetch<DraftContent>(`/api/drafts/${id}/content${qs}`);
}

export async function listDraftVersions(id: string) {
  return apiFetch<{ draft_id: string; current_version: number; versions: DraftVersionMeta[] }>(
    `/api/drafts/${id}/versions`,
  );
}

export async function saveDraftVersion(
  id: string,
  input: { html: string; design_json?: string | null; summary?: string },
) {
  return apiFetch<{ ok: true; versionFrom: number; versionTo: number }>(
    `/api/drafts/${id}/save`,
    { method: 'POST', json: input },
  );
}

export async function autoSaveDraft(
  id: string,
  input: { html: string; design_json?: string | null },
) {
  return apiFetch<{ ok: true; version: number }>(
    `/api/drafts/${id}/autosave`,
    { method: 'PUT', json: input },
  );
}

export async function patchDraft(
  id: string,
  input: Partial<{
    title: string | null;
    subject: string | null;
    preview_text: string | null;
    from_email: string | null;
    from_name: string | null;
    status: DraftStatus;
  }>,
) {
  return apiFetch<{ ok: true }>(`/api/drafts/${id}`, { method: 'PATCH', json: input });
}

export async function deleteDraft(id: string) {
  return apiFetch<{ ok: true }>(`/api/drafts/${id}`, { method: 'DELETE' });
}

export function formatRelativeTime(ts: number): string {
  if (!Number.isFinite(ts) || ts <= 0) return '—';
  const diff = Date.now() - ts;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(ts).toLocaleDateString();
}
